<template>
  <div class="mcontainer">
    <!-- Header -->
    <div class="header-bar">
      <h2>Business Opportunity</h2>
      <div class="actions">
        <div class="search">
          <Icon icon="mdi:magnify" width="18" height="18" class="search-icon" />
          <input
            class="search-input"
            v-model="search"
            @input="debouncedFetch"
            placeholder="Search opportunity..."
          />
        </div>
        <button v-if="user && user.type === 'alumni'" class="btn btn-primary" @click="openModal">
          <Icon icon="mdi:plus" width="18" height="18" />
          <span>Submit</span>
        </button>
      </div>
    </div>

    <!-- Card grid -->
    <div class="card">
      <div class="grid">
        <div
          v-for="item in list"
          :key="item.id"
          class="bo-card"
          role="button"
          tabindex="0"
          @click="openDetail(item)"
          @keyup.enter="openDetail(item)"
        >
          <img v-if="getImage(item)" :src="getImage(item)" alt="" class="bo-img" />
          <div v-else class="bo-img placeholder">
            <Icon icon="mdi:image-off-outline" width="22" height="22" />
          </div>
          <div class="bo-body">
            <h4 class="bo-title">{{ item.name }}</h4>
            <div class="meta">
              by {{ (item.user && item.user.fullname) || 'Unknown' }}
            </div>
          </div>
        </div>
      </div>

      <div class="loading-wrap" v-if="loading"><b-spinner small/></div>

      <!-- Pagination -->
      <div v-if="totalPage > 1" class="pager-wrap">
        <div class="pager-info">Page {{ page }} of {{ totalPage }}</div>
        <div class="pager-controls">
          <button class="icon-btn" :disabled="page===1 || loading" @click="go(1)" title="First">
            <Icon icon="mdi:chevron-double-left" width="18" height="18" />
          </button>
          <button class="icon-btn" :disabled="page===1 || loading" @click="go(page-1)" title="Prev">
            <Icon icon="mdi:chevron-left" width="18" height="18" />
          </button>

          <button
            v-for="p in pages"
            :key="p"
            class="num-btn"
            :class="{ active: p === page }"
            :disabled="p === page || loading"
            @click="go(p)"
          >
            {{ p }}
          </button>

          <button class="icon-btn" :disabled="page===totalPage || loading" @click="go(page+1)" title="Next">
            <Icon icon="mdi:chevron-right" width="18" height="18" />
          </button>
          <button class="icon-btn" :disabled="page===totalPage || loading" @click="go(totalPage)" title="Last">
            <Icon icon="mdi:chevron-double-right" width="18" height="18" />
          </button>
        </div>
      </div>
    </div>

    <!-- Modal submit -->
    <div v-if="open" class="modal-overlay" @click.self="close">
      <div class="modal-panel">
        <div class="modal-header">
          <h3>Submit Business Opportunity</h3>
          <button class="icon-btn sm" @click="close">
            <Icon icon="mdi:close" width="18" height="18" />
          </button>
        </div>
        <div class="modal-body">
          <div class="form-row">
            <label>Nama</label>
            <input class="form-control" v-model="form.name" />
          </div>
          <div class="form-row">
            <label>Gambar</label>
            <input type="file" class="form-control" accept="image/*" @change="onFile" />
          </div>
          <div class="form-row">
            <label>Deskripsi</label>
            <textarea class="form-control" rows="5" v-model="form.description"></textarea>
          </div>
          <div class="muted">Submission akan direview admin terlebih dahulu.</div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-light" @click="close">Batal</button>
          <button class="btn btn-primary" :disabled="submitting" @click="submit">
            {{ submitting ? 'Mengirim...' : 'Kirim' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Modal detail -->
    <div v-if="detailOpen" class="modal-overlay" @click.self="closeDetail">
      <div class="modal-panel modal-lg">
        <div class="modal-header">
          <h3>Detail Opportunity</h3>
          <button class="" @click="closeDetail">
            <Icon icon="mdi:close" width="18" height="18" />
          </button>
        </div>
        <div class="modal-body detail-body">
          <div class="detail-media">
            <img v-if="getImage(selected)" :src="getImage(selected)" class="detail-img" />
            <div v-else class="detail-img placeholder">
              <Icon icon="mdi:image-off-outline" width="28" height="28" />
            </div>
          </div>
          <div class="detail-content">
            <h3 class="detail-title">{{ selected && selected.name }}</h3>
            <div class="detail-meta">
              <span>Submitted by: <b>{{ selected && selected.user ? selected.user.fullname : 'Unknown' }}</b></span>
              <!-- <span v-if="selected && selected.status" class="status-badge" :class="'status-' + selected.status">
                {{ selected.status }}
              </span> -->
            </div>
            <p class="detail-desc">{{ selected && selected.description }}</p>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-light" @click="closeDetail">Tutup</button>
        </div>
      </div>
    </div>
    <!-- End Modal detail -->
  </div>
</template>

<script>
import axios from 'axios'
import { Icon } from '@iconify/vue2'

export default {
  name: 'BusinessOpportunity',
  components: { Icon },
  data() {
    return {
      list: [],
      loading: false,
      search: '',
      page: 1,
      totalPage: 1,
      perPage: 12,
      open: false,
      form: { name: '', description: '' },
      imageFile: null,
      submitting: false,
      isAlumni: false,
      debouncedFetch: null,

      // detail modal
      detailOpen: false,
      selected: null
    }
  },
  created() {
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}')
      this.isAlumni = String((u && u.type) || '').toLowerCase() === 'alumni'
    } catch (e) {
      this.isAlumni = false
    }
    this.debouncedFetch = this.debounce(this.fetchList, 500)
    this.fetchList()
  },
  computed: {
    pages() {
      const t = this.totalPage
      const cur = this.page
      const win = 5
      const arr = []
      let s = Math.max(1, cur - 2)
      let e = Math.min(t, s + win - 1)
      if (e - s < win - 1) s = Math.max(1, e - win + 1)
      for (let i = s; i <= e; i++) arr.push(i)
      return arr
    },
    user () {
      return this.$store.state.auth.user
    }
  },
  methods: {
    // ambil url gambar, fallback ke image_url atau image
    getImage(item) {
      if (!item) return null
      if (item.image_url) return item.image_url
      if (item.image) return item.image
      return null
    },
    truncate(s, n) { return s && s.length > n ? s.slice(0, n) + '...' : (s || '') },
    debounce(fn, d) { let t = null; return (...a) => { clearTimeout(t); t = setTimeout(() => fn.apply(this, a), d) } },
    async fetchList() {
      this.loading = true
      try {
        const params = new URLSearchParams()
        params.set('page', this.page)
        params.set('per_page', this.perPage)
        if (this.search) params.set('search', this.search)
        const { data } = await axios.get(`/bo?${params.toString()}`)
        this.list = (data && data.data) ? data.data : []
        this.page = (data && data.current_page) ? data.current_page : 1
        this.totalPage = (data && data.last_page) ? data.last_page : 1
      } finally {
        this.loading = false
      }
    },
    go(p) { if (p < 1 || p > this.totalPage || p === this.page) return; this.page = p; this.fetchList() },

    // submit modal
    openModal() { this.open = true; this.form = { name: '', description: '' }; this.imageFile = null },
    close() { this.open = false },
    onFile(e) { this.imageFile = (e.target && e.target.files && e.target.files[0]) ? e.target.files[0] : null },
    async submit() {
      if (!this.form.name) { alert('Nama wajib diisi'); return }
      this.submitting = true
      try {
        const fd = new FormData()
        fd.append('name', this.form.name)
        fd.append('description', this.form.description || '')
        if (this.imageFile) fd.append('image', this.imageFile)
        // biarkan browser set boundary
        await axios.post('/bo', fd)
        this.close()
        this.page = 1
        this.fetchList()
        if (this.$swal) this.$swal({ icon: 'success', title: 'Terkirim', text: 'Menunggu persetujuan admin' })
      } catch (e) {
        if (this.$swal) this.$swal({ icon: 'error', title: 'Gagal', text: 'Periksa kembali input kamu' })
      } finally {
        this.submitting = false
      }
    },

    // detail modal
    openDetail(item) {
      // jika API list belum include description full dan kamu punya endpoint detail:
      // this.fetchDetail(item.id) ...
      this.selected = item
      this.detailOpen = true
    },
    closeDetail() {
      this.detailOpen = false
      this.selected = null
    },
    // contoh jika nanti mau ambil detail dari endpoint terpisah:
    // async fetchDetail(id) {
    //   const { data } = await axios.get(`/bo/${id}`)
    //   this.selected = data && data.data ? data.data : null
    //   this.detailOpen = true
    // }
  }
}
</script>

<style scoped>
.header-bar{display:flex;align-items:center;justify-content:space-between;margin-bottom:12px}
.actions{display:flex;gap:10px;align-items:center}
.search{position:relative;min-width:260px;width:340px}
.search-icon{position:absolute;top:10px;left:12px;color:#9ca3af}
.search-input{width:100%;height:38px;border:1px solid #e5e7eb;border-radius:10px;padding:0 12px 0 36px;background:#fff}
.card{background:#fff;border:1px solid #eef2f7;border-radius:14px;padding:16px}
.grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(260px,1fr));gap:12px}
.bo-card{border:1px solid #eef2f7;border-radius:12px;overflow:hidden;background:#fff;cursor:pointer}
.bo-card:focus{outline:2px solid #93c5fd; outline-offset: 2px}
.bo-img{width:100%;height:160px;object-fit:cover}
.bo-img.placeholder{display:flex;align-items:center;justify-content:center;background:#fafafa;color:#9ca3af}
.bo-body{padding:10px}
.bo-title{margin:0 0 6px 0;font-size:16px}
.muted{color:#6b7280}
.meta{margin-top:4px;font-size:12px;color:#9ca3af}
.btn{height:38px;padding:0 12px;border-radius:10px;border:1px solid #e5e7eb;background:#fff;display:inline-flex;gap:8px;align-items:center}
.btn-primary{background:#2563eb;border-color:#2563eb;color:#fff}
.loading-wrap{display:flex;justify-content:center;padding:10px}
.pager-wrap{display:flex;align-items:center;justify-content:space-between;margin-top:12px}
.icon-btn{height:36px;width:36px;border:1px solid #e5e7eb;background:#fff;border-radius:10px}
.num-btn{min-width:36px;height:36px;border:1px solid #e5e7eb;background:#fff;border-radius:10px}
.num-btn.active{background:#2563eb;border-color:#2563eb;color:#fff}

/* Modal */
.modal-overlay{position:fixed;inset:0;background:rgba(15,23,42,.25);display:flex;align-items:center;justify-content:center;padding:24px 16px;z-index:999;overflow-y:auto}
.modal-panel{width:100%;max-width:560px;max-height:calc(100vh - 48px);background:#fff;border:1px solid #eef2f7;border-radius:14px;overflow:hidden;box-shadow:0 16px 40px rgba(2,6,23,.15);display:flex;flex-direction:column}
.modal-lg{max-width:900px}
.modal-header{display:flex;justify-content:space-between;align-items:center;padding:12px 16px;border-bottom:1px solid #eef2f7;flex-shrink:0}
.modal-body{padding:12px 16px;overflow-y:auto;flex:1;min-height:0}
.modal-footer{display:flex;justify-content:flex-end;gap:10px;padding:12px 16px;border-top:1px solid #eef2f7;flex-shrink:0}
.form-row{display:flex;flex-direction:column;gap:6px;margin-bottom:10px}
.form-control{height:38px;border:1px solid #e5e7eb;border-radius:10px;padding:0 10px;background:#fff}
textarea.form-control{height:auto;padding:8px 10px}

/* Detail layout */
.detail-body{display:grid;grid-template-columns:360px 1fr;gap:16px}
.detail-media{}
.detail-img{width:100%;height:260px;object-fit:cover;border-radius:12px;border:1px solid #eef2f7;background:#fafafa}
.detail-title{margin:0 0 8px 0}
.detail-meta{display:flex;gap:10px;align-items:center;margin-bottom:8px;color:#6b7280}
.status-badge{display:inline-block;padding:2px 8px;border-radius:9999px;font-size:12px;text-transform:capitalize;border:1px solid #e5e7eb}
.status-pending{background:#fff7ed;color:#9a3412;border-color:#fed7aa}
.status-approved{background:#ecfdf5;color:#065f46;border-color:#a7f3d0}
.status-rejected{background:#fef2f2;color:#991b1b;border-color:#fecaca}
.detail-desc{white-space:pre-line}

/* Responsive */
@media (max-width:768px){
  .actions{width:100%}
  .search{width:100%;min-width:0}
  .header-bar{flex-direction:column;align-items:stretch;gap:8px}
  .detail-body{grid-template-columns:1fr}
}
</style>